import {
  PROTOCOL_VERSION,
  decodeChunkFrame,
  type JsonMessage,
  type WireFileMeta,
} from '../../../shared/protocol';
import type { Transfer } from './contract';
import type { ReceiveStore } from './storage';
import { finishReceive, openReceive, writeReceive } from './storage';

export interface IncomingRuntime {
  kind: 'receive';
  store?: ReceiveStore;
  completeReceived: boolean;
  finishing?: boolean;
  acked: number[];
  bytesSinceAck: number[];
}

export interface IncomingTransferContext {
  getTransfer(id: string): Transfer | undefined;
  setTransferPath(transferId: string, path: 'direct' | 'relay'): void;
  emit(): void;
  persist(): void;
  holdWakeLock(): void;
  releaseWakeLock(): void;
  sendJson(message: JsonMessage): void;
  sendControl(peerId: string, message: JsonMessage): void;
  ensureRtc(peerId: string, initiator: boolean): void;
  mode: 'relay' | 'webrtc' | 'auto';
}

export class IncomingTransferManager {
  private readonly runtimes = new Map<string, IncomingRuntime>();

  constructor(private readonly ctx: IncomingTransferContext) {}

  register(transferId: string, runtime: IncomingRuntime): void {
    this.runtimes.set(transferId, runtime);
  }

  get(transferId: string): IncomingRuntime | undefined {
    return this.runtimes.get(transferId);
  }

  delete(transferId: string): boolean {
    return this.runtimes.delete(transferId);
  }

  accept(transferId: string): void {
    const transfer = this.ctx.getTransfer(transferId);
    const runtime = this.runtimes.get(transferId);
    if (!transfer || !runtime || transfer.state !== 'incoming') return;

    transfer.state = 'transferring';
    transfer.path = this.ctx.mode === 'relay' ? 'relay' : 'connecting';
    this.ctx.emit();

    void openReceive(transferId, transfer.files).then((store) => {
      runtime.store = store;
      this.ctx.sendJson({
        v: PROTOCOL_VERSION,
        type: 'transfer.accept',
        to: transfer.peerId,
        transferId,
      });
      this.ctx.sendJson({
        v: PROTOCOL_VERSION,
        type: 'transfer.credit',
        to: transfer.peerId,
        transferId,
        bytes: 8 * 1024 * 1024,
      });
      if (this.ctx.mode !== 'relay') {
        this.ctx.ensureRtc(transfer.peerId, false);
      }
      if (this.ctx.mode === 'relay') {
        this.ctx.setTransferPath(transferId, 'relay');
      }
    });

    this.ctx.holdWakeLock();
  }

  decline(transferId: string): void {
    const transfer = this.ctx.getTransfer(transferId);
    if (!transfer) return;

    transfer.state = 'declined';
    transfer.finishedAt = Date.now();
    this.ctx.sendJson({
      v: PROTOCOL_VERSION,
      type: 'transfer.decline',
      to: transfer.peerId,
      transferId,
    });
    this.ctx.emit();
    this.ctx.persist();
    this.ctx.releaseWakeLock();
  }

  cancel(transferId: string): void {
    const transfer = this.ctx.getTransfer(transferId);
    if (!transfer) return;

    transfer.state = 'cancelled';
    transfer.finishedAt = Date.now();
    this.ctx.sendJson({
      v: PROTOCOL_VERSION,
      type: 'transfer.cancel',
      to: transfer.peerId,
      transferId,
    });
    this.ctx.emit();
    this.ctx.persist();
    this.ctx.releaseWakeLock();
  }

  async onChunk(peerId: string, bytes: Uint8Array, isRelay = false): Promise<void> {
    const frame = decodeChunkFrame(bytes);
    if (!frame) return;

    const transfer = this.ctx.getTransfer(frame.transferId);
    const runtime = this.runtimes.get(frame.transferId);
    if (!transfer || !runtime || transfer.peerId !== peerId || !runtime.store) {
      return;
    }

    if (isRelay) {
      if (transfer.path !== 'relay') transfer.path = 'relay';
    } else {
      if (transfer.path !== 'direct') transfer.path = 'direct';
    }

    await writeReceive(runtime.store, frame.fileIndex, frame.offset, frame.payload);

    runtime.acked[frame.fileIndex] = Math.max(
      runtime.acked[frame.fileIndex],
      frame.offset + frame.payload.byteLength,
    );
    runtime.bytesSinceAck[frame.fileIndex] =
      (runtime.bytesSinceAck[frame.fileIndex] ?? 0) + frame.payload.byteLength;

    transfer.bytesDone = runtime.acked.reduce((sum, v) => sum + v, 0);
    const elapsedSec = Math.max(0.001, (Date.now() - transfer.startedAt) / 1000);
    transfer.bytesPerSec = transfer.bytesDone / elapsedSec;
    transfer.etaSec =
      transfer.bytesPerSec > 0
        ? Math.max(0, (transfer.totalBytes - transfer.bytesDone) / transfer.bytesPerSec)
        : null;

    if (transfer.path === 'relay' || this.ctx.mode === 'relay') {
      this.ctx.sendJson({
        v: PROTOCOL_VERSION,
        type: 'transfer.credit',
        to: peerId,
        transferId: transfer.id,
        bytes: frame.payload.byteLength,
      });
    }

    const currentOffset = runtime.store.offsets[frame.fileIndex];
    const targetSize = transfer.files[frame.fileIndex].size;
    const isDone = currentOffset >= targetSize;
    const shouldAck = runtime.bytesSinceAck[frame.fileIndex] >= 1024 * 1024 || isDone;

    if (shouldAck) {
      runtime.bytesSinceAck[frame.fileIndex] = 0;
      this.ctx.sendControl(peerId, {
        v: PROTOCOL_VERSION,
        type: 'transfer.ack',
        to: peerId,
        transferId: transfer.id,
        fileIndex: frame.fileIndex,
        offset: Math.min(currentOffset, targetSize),
      });
    }

    this.ctx.emit();

    const allFinished = runtime.store.offsets.every(
      (off, index) => off >= transfer.files[index].size,
    );
    if (allFinished) {
      await this.finishIncoming(transfer.id);
    }
  }

  handleComplete(peerId: string, transferId: string): void {
    const runtime = this.runtimes.get(transferId);
    const transfer = this.ctx.getTransfer(transferId);
    if (!runtime || !transfer || transfer.peerId !== peerId) return;

    runtime.completeReceived = true;
    if (
      runtime.store &&
      runtime.store.offsets.every((off, index) => off >= transfer.files[index].size)
    ) {
      void this.finishIncoming(transferId);
    }
  }

  async finishIncoming(transferId: string): Promise<void> {
    const transfer = this.ctx.getTransfer(transferId);
    const runtime = this.runtimes.get(transferId);
    if (!transfer || !runtime || !runtime.store || runtime.finishing ||
      ['done', 'failed', 'cancelled', 'declined'].includes(transfer.state)) return;

    // The final chunk and transfer.complete can arrive while storage is closing.
    // Claim completion synchronously so only one path closes the file writers.
    runtime.finishing = true;
    try {
      await finishReceive(runtime.store);
      if (transfer.state === 'cancelled') return;
      transfer.state = 'done';
      transfer.verified = true;
      transfer.bytesDone = transfer.totalBytes;
    } catch {
      transfer.state = 'failed';
      transfer.error = 'Could not finish saving these files. Free some space and try again.';
    } finally {
      transfer.finishedAt = Date.now();
      this.ctx.emit();
      this.ctx.persist();
      this.ctx.releaseWakeLock();
    }
  }
}
